import { useState, useEffect, useRef } from 'react'

// Status enum mapping to match backend IntegrationStatus
const getStatusNameFromValue = (statusValue: number): string => {
  const statusMap: { [key: number]: string } = {
    0: 'credential_error',
    1: 'active',
    2: 'no_content',
    3: 'error',
    4: 'oauth_stub',
    5: 'verifying',
    6: 'revoked',
    7: 'pending_credentials'
  }
  return statusMap[statusValue] || ''
}

// Slugify integration name for the new format {id}|{name_slug}
const slugifyName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// Calculate days from date range (for backfill period display)
const calculateDaysFromRange = (startDate: string | null, endDate: string | null): number | null => {
  if (!startDate || !endDate) return null
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  return diffDays
}

import { useNavigate, useParams } from 'react-router-dom'
import { useOrganization } from '@/contexts/organization-context'
import { integrationsService, IntegrationType, ActiveBackfillDetails } from '@/services/integrations'
import { integratorsService } from '@/services/integrators'
import { IntegratorType } from '@/services/integrations'
import { api } from '@/services/api'
import { monitoringService, JobStatus, JobSearchResult } from '@/services/monitoring'
import { JobProgressBar } from '@/components/monitoring/shared/JobProgressBar'
import { type DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
// import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import { OrderedMultiSelect } from '@/components/ui/ordered-multi-select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import {
  ArrowRightLeft,
  Trash2,
  Calendar,
  Lock,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Database,
  Plus,
  PlayCircle,
  X,
  Eye,
  EyeOff,
  Asterisk,
  Activity,
  RefreshCw,
  Filter,
  Copy,
  MoreVertical,
  Tag,
  History
} from 'lucide-react'

// Helper function to get today's date
const getTodayDate = (): Date => {
  return new Date()
}

export function EditIntegrationPage() {
  const navigate = useNavigate()
  const { orgSlug, integrationId } = useParams<{ orgSlug: string, integrationId: string }>()
  const { currentOrganization } = useOrganization()
  
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [integration, setIntegration] = useState<IntegrationType | null>(null)
  const [integrator, setIntegrator] = useState<IntegratorType | null>(null)
  const [primitives, setPrimitives] = useState<any[]>([])
  const [primitivesLoaded, setPrimitivesLoaded] = useState(false)
  const [credentialDefinitions, setCredentialDefinitions] = useState<any[]>([])
  const [credentialValues, setCredentialValues] = useState<{ [key: string]: string }>({})
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({})
  const [revealingPassword, setRevealingPassword] = useState<Record<string, boolean>>({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [backfillDialogOpen, setBackfillDialogOpen] = useState(false)
  const [backfillDateRange, setBackfillDateRange] = useState<DateRange | undefined>(undefined)
  const [revokeConfirmDialogOpen, setRevokeConfirmDialogOpen] = useState(false)
  const [cancelBackfillDialogOpen, setCancelBackfillDialogOpen] = useState(false)
  const [cancellingBackfill, setCancellingBackfill] = useState(false)
  const [revokeAuthDialogOpen, setRevokeAuthDialogOpen] = useState(false)
  const [revokingAuth, setRevokingAuth] = useState(false)
  const [authorizations, setAuthorizations] = useState<any[]>([])
  const [selectedAuthorizationId, setSelectedAuthorizationId] = useState<string>('')
  const [loadingAuthorizations, setLoadingAuthorizations] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('credentials')

  // Refs to prevent duplicate saves
  const savingNameRef = useRef(false)
  const originalNameRef = useRef<string>('')

  // Backfill progress tracking
  const [backfillJobIds, setBackfillJobIds] = useState<number[]>([])
  const [backfillProgress, setBackfillProgress] = useState<JobStatus | null>(null)
  const [backfillPollingActive, setBackfillPollingActive] = useState(false)
  const [activeBackfill, setActiveBackfill] = useState<ActiveBackfillDetails | null>(null)

  // Jobs tracking for Activities tab
  const [jobs, setJobs] = useState<JobSearchResult[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobsTotal, setJobsTotal] = useState(0)

  // Collection stats for total records
  const [collectionStats, setCollectionStats] = useState<{ recs: number; last_date: string | null } | null>(null)
  // Job statuses filter: 'queued', 'running', 'completed', 'failed'
  // Default to all statuses
  const [jobStatusFilter, setJobStatusFilter] = useState<Set<string>>(new Set(['queued', 'running', 'completed', 'failed']))

  // Available tags for tag selector (fetched from existing integrations)
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [tagInputValue, setTagInputValue] = useState('')

  // Track mobile screen size for responsive calendar
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Set default date range when backfill dialog opens (last 30 days ending yesterday)
  useEffect(() => {
    if (backfillDialogOpen) {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 31)
      setBackfillDateRange({ from: thirtyDaysAgo, to: yesterday })
    }
  }, [backfillDialogOpen])

  // Poll for backfill progress when active
  useEffect(() => {
    if (!backfillPollingActive || backfillJobIds.length === 0) return

    let pollCount = 0

    // Fast backfills can finish before the active-jobs poll observes them, so the
    // live counter never advances past its initial 0. On completion, fetch the
    // final record count for the backfill job(s) directly.
    const fetchFinalRecords = async (): Promise<number> => {
      const results = await Promise.all(
        backfillJobIds.map(id => monitoringService.getJobProgress(id).catch(() => null))
      )
      return results.reduce((sum, p) => sum + (p?.records_processed ?? 0), 0)
    }

    const pollProgress = async () => {
      pollCount++
      try {
        const activeJobs = await monitoringService.getActiveJobs({
          integration_id: integration?.id
        })

        // Find job matching our backfill
        const backfillJob = activeJobs.jobs.find(job =>
          backfillJobIds.includes(job.job_id)
        )

        if (backfillJob) {
          setBackfillProgress(prev => ({
            ...backfillJob,
            period_start: prev?.period_start,
            period_end: prev?.period_end
          }))

          // Check if completed
          if (backfillJob.status === 'success' || backfillJob.status === 'failed') {
            setBackfillPollingActive(false)
            setActiveBackfill(null) // Clear active backfill to re-enable button
            // The active-jobs poll may report 0 records for a just-finished job;
            // pull the final count so the banner reflects what was processed.
            const finalRecords = await fetchFinalRecords()
            if (finalRecords > 0) {
              setBackfillProgress(prev => prev ? { ...prev, records_processed: finalRecords } : prev)
            }
            if (backfillJob.status === 'success') {
              toast.success('Backfill completed successfully!')
            } else {
              toast.error('Backfill failed. Check monitoring dashboard for details.')
            }
            // Refresh integration status after backfill completes
            try {
              const updatedIntegration = await integrationsService.show(Number(integrationId))
              setIntegration(updatedIntegration)
            } catch (e) {
              console.error('Failed to refresh integration status:', e)
            }
          }
        } else {
          // Job not in active list - it likely completed already
          // After a few polls without finding the job, assume it's done
          if (pollCount >= 2) {
            setBackfillPollingActive(false)
            setActiveBackfill(null) // Clear active backfill to re-enable button
            // Job already left the active list (fast completion) - fetch the
            // final record count instead of leaving the banner at 0.
            const finalRecords = await fetchFinalRecords()
            setBackfillProgress(prev => prev ? {
              ...prev,
              status: 'success',
              progress_pct: 100,
              periods_completed: prev.total_periods, // Set completed = total
              records_processed: finalRecords || prev.records_processed
            } : null)
            toast.success('Backfill completed!')
            // Refresh integration status after backfill completes
            try {
              const updatedIntegration = await integrationsService.show(Number(integrationId))
              setIntegration(updatedIntegration)
            } catch (e) {
              console.error('Failed to refresh integration status:', e)
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll backfill progress:', error)
      }
    }

    // Initial poll
    pollProgress()

    // Set up interval
    const interval = setInterval(pollProgress, 3000) // Poll every 3 seconds

    return () => clearInterval(interval)
  }, [backfillPollingActive, backfillJobIds, integration?.id])

  // Load integration and integrator data
  useEffect(() => {
    if (currentOrganization && integrationId) {
      loadData()
    }
  }, [currentOrganization, integrationId])

  // Fetch authorizations when integrator is OAuth2 type
  useEffect(() => {
    if (integrator && integrator.technology === 'api_oauth2') {
      fetchAuthorizations()
    } else {
      setAuthorizations([])
      setSelectedAuthorizationId('')
    }
  }, [integrator])

  // Update selected authorization when integration authorization_id changes
  useEffect(() => {
    if (integration?.authorization_id && authorizations.length > 0) {
      const currentAuth = authorizations.find((auth: any) => auth.id === integration.authorization_id)
      if (currentAuth) {
        setSelectedAuthorizationId(integration.authorization_id.toString())
      }
    }
  }, [integration?.authorization_id, authorizations])

  const fetchAuthorizations = async () => {
    if (!integrator || integrator.technology !== 'api_oauth2') {
      setAuthorizations([])
      return
    }

    console.log('Fetching authorizations for integrator:', integrator.id, 'integration:', integration?.id)
    setLoadingAuthorizations(true)
    try {
      
      // Get all authorizations for this integrator
      const response = await api.get(`/oauth/authorizations?integrator_id=${integrator.id}`)
      const authList = response.data.authorizations || []
      console.log('Found authorizations:', authList.length, authList)
      
      // Try to get the specific authorization for this integration (only if one exists)
      let currentAuthId: string | null = null
      if (integration?.authorization_id) {
        try {
          const integrationAuthResponse = await api.get(`/integrations/${integration?.id}/authorization`)
          if (integrationAuthResponse.data && integrationAuthResponse.data.authorization) {
            currentAuthId = integrationAuthResponse.data.authorization.id

            // Add the current authorization to the list if it's not already there
            const authExists = authList.find((auth: any) => auth.id === currentAuthId)
            if (!authExists && integrationAuthResponse.data.authorization) {
              authList.unshift(integrationAuthResponse.data.authorization)
            }
          }
        } catch (integrationAuthError) {
        }
      }
      
      setAuthorizations(authList)
      
      // Set the current authorization with improved detection logic
      if (currentAuthId) {
        setSelectedAuthorizationId(currentAuthId.toString())
      } else if (integration?.authorization_id) {
        const currentAuth = authList.find((auth: any) => auth.id === integration.authorization_id)
        if (currentAuth) {
          setSelectedAuthorizationId(integration.authorization_id.toString())
        }
      } else {
        // Try multiple strategies to find the correct authorization
        
        // 1. Find authorization by integration_id (direct link)
        const authByIntegration = authList.find((auth: any) => auth.integration_id === integration?.id)
        if (authByIntegration) {
          setSelectedAuthorizationId(authByIntegration.id.toString())
          return // Found it, no need to try other methods
        }
        
        // 2. Find authorization by name pattern (e.g., "Google Ads (#1423)")
        const authByName = authList.find((auth: any) => 
          auth.name && auth.name.includes(`(#${integration?.id})`)
        )
        if (authByName) {
          setSelectedAuthorizationId(authByName.id.toString())
          return
        }
        
        // 3. Don't auto-select any authorization on initial load
        // Authorization should only be selected when:
        // - The integration already has an authorization_id
        // - OAuth completion triggers a refresh (handled by message listener)
        // - User manually selects an authorization
      }
    } catch (error) {
      console.error('Failed to fetch authorizations:', error)
      setAuthorizations([])
    } finally {
      setLoadingAuthorizations(false)
    }
  }
  
  const loadData = async () => {
    setLoading(true)
    // Reset backfill state when loading a new integration
    setActiveBackfill(null)
    setBackfillProgress(null)
    setBackfillPollingActive(false)
    setBackfillJobIds([])

    const startTime = performance.now()
    const logTiming = (label: string, start: number) => {
      console.log(`[Edit Integration] ${label}: ${(performance.now() - start).toFixed(0)}ms`)
    }

    try {
      // PHASE 1: Load integration details (required to show page)
      const stepStart = performance.now()
      const integrationData = await integrationsService.show(Number(integrationId))
      logTiming('1. Integration details (critical)', stepStart)
      setIntegration(integrationData)
      originalNameRef.current = integrationData.name || ''

      // Show page immediately - don't wait for secondary data
      setLoading(false)

      // PHASE 2: Load all secondary data in parallel (background)
      const parallelStart = performance.now()
      const orgSlugFromPath = location.pathname.split('/')[1]

      await Promise.all([
        // 2. Active backfill check
        (async () => {
          const s = performance.now()
          try {
            const activeBackfillResponse = await integrationsService.getActiveBackfill(Number(integrationId))
            logTiming('2. Active backfill check', s)
            if (activeBackfillResponse.active_backfill) {
              setActiveBackfill(activeBackfillResponse.active_backfill)
              setBackfillProgress({
                job_id: activeBackfillResponse.active_backfill.job_id,
                integration_id: activeBackfillResponse.active_backfill.integration_id,
                integration_name: integrationData.name,
                organization_id: 0,
                organization_name: '',
                current_step: 'collect',
                status: activeBackfillResponse.active_backfill.status === 'running' ? 'running' : 'queued',
                progress_pct: activeBackfillResponse.active_backfill.progress_pct,
                records_processed: 0,
                eta_seconds: 0,
                started_at: activeBackfillResponse.active_backfill.created_at || '',
                periods_completed: activeBackfillResponse.active_backfill.completed_periods,
                total_periods: activeBackfillResponse.active_backfill.total_periods,
                periods_failed: activeBackfillResponse.active_backfill.failed_periods,
                period_start: activeBackfillResponse.active_backfill.period_start,
                period_end: activeBackfillResponse.active_backfill.period_end
              })
              setBackfillJobIds([activeBackfillResponse.active_backfill.job_id])
              setBackfillPollingActive(true)
            }
          } catch (error) {
            console.error('Failed to check active backfill:', error)
          }
        })(),

        // 3. Integrator details
        (async () => {
          if (!integrationData.integrator_id) return
          const s = performance.now()
          try {
            const currentIntegrator = await integratorsService.getById(integrationData.integrator_id)
            logTiming('3. Integrator details', s)
            setIntegrator(currentIntegrator || null)
          } catch (error) {
            console.error('Failed to load integrator:', error)
          }
        })(),

        // 4. Credential definitions
        (async () => {
          if (!integrationData.integrator_id) return
          const s = performance.now()
          try {
            const credentialsResponse = await api.get(`/integrators/${integrationData.integrator_id}/credentials`)
            logTiming('4. Credential definitions', s)
            if (credentialsResponse.data?.credentials) {
              setCredentialDefinitions(credentialsResponse.data.credentials)
            }
          } catch (error) {
            console.error('Failed to load credential definitions:', error)
          }
        })(),

        // 5. Integration credentials
        (async () => {
          if (!integrationData.integrator_id) return
          const s = performance.now()
          try {
            const credentialsPath = orgSlugFromPath && orgSlugFromPath !== 'integrations'
              ? `/${orgSlugFromPath}/integrations/${integrationData.id}/credentials`
              : `/integrations/${integrationData.id}/credentials`
            const integrationCredentialsResponse = await api.get(credentialsPath)
            logTiming('5. Integration credentials', s)
            if (integrationCredentialsResponse.data?.credentials) {
              const existingValues: { [key: string]: string } = {}
              integrationCredentialsResponse.data.credentials.forEach((cred: any) => {
                if (cred.name && cred.value !== undefined) {
                  if (cred.is_password && cred.value === "***configured***") {
                    existingValues[cred.name] = "***configured***"
                  } else if (!cred.is_password) {
                    existingValues[cred.name] = cred.value
                  }
                }
              })
              if (integrationData.integrator_id === 151 && !existingValues['report_type']) {
                existingValues['report_type'] = 'network'
              }
              setCredentialValues(existingValues)
            }
          } catch (error) {
            console.error('Failed to load existing credential values:', error)
            if (integrationData.integrator_id === 151) {
              setCredentialValues({ 'report_type': 'network' })
            }
          }
        })(),

        // 6. Collection stats
        (async () => {
          const s = performance.now()
          try {
            const collectionStatsResponse = await integrationsService.getCollectionStatsForIntegration(Number(integrationId))
            logTiming('6. Collection stats', s)
            if (collectionStatsResponse.collection_stats) {
              const stats = collectionStatsResponse.collection_stats
              setCollectionStats({ recs: stats.recs, last_date: stats.last_date })
            }
          } catch (error) {
            console.error('Failed to load collection stats:', error)
          }
        })(),

        // 7. Available tags
        (async () => {
          const s = performance.now()
          try {
            const tagsResponse = await integrationsService.getAllTags()
            logTiming('7. Available tags', s)
            const tags = new Set<string>(tagsResponse.tags || [])
            if (integrationData.tag) {
              tags.add(integrationData.tag)
            }
            setAvailableTags(Array.from(tags).sort())
          } catch (error) {
            console.error('Failed to load available tags:', error)
            if (integrationData.tag) {
              setAvailableTags([integrationData.tag])
            }
          }
        })(),
      ])

      logTiming('PARALLEL phase (2-7)', parallelStart)
      logTiming('TOTAL load time', startTime)

    } catch (error) {
      console.error('Failed to load integration:', error)
      toast.error('Failed to load integration')
      navigate(`/${orgSlug}/integrations`)
      setLoading(false)
    }
  }

  // Fetch jobs for this integration - last 7 days
  const fetchJobs = async () => {
    if (!integration?.id) return

    setJobsLoading(true)
    try {
      // Get jobs from the last 7 days
      const now = new Date()
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

      const response = await monitoringService.searchJobs({
        integration_id: integration.id,
        start_date: sevenDaysAgo.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
        status: Array.from(jobStatusFilter).join(','),
        limit: 50,
        offset: 0
      })

      setJobs(response.jobs)
      setJobsTotal(response.total)
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err)
      // Don't show error toast, just log it
    } finally {
      setJobsLoading(false)
    }
  }

  // Fetch jobs when tab changes to activities or filter changes
  // Only fetch after main page loading is complete to avoid race conditions
  useEffect(() => {
    if (activeTab === 'activities' && integration?.id && !loading) {
      fetchJobs()
    }
  }, [activeTab, integration?.id, loading, jobStatusFilter])

  // Lazy load primitives when mapping tab is opened
  useEffect(() => {
    if (activeTab === 'mapping' && !primitivesLoaded) {
      const loadPrimitives = async () => {
        try {
          const primitivesResponse = await api.get('/data/primitives')
          if (primitivesResponse.data && primitivesResponse.data.primitives) {
            setPrimitives(primitivesResponse.data.primitives)
          }
        } catch (error) {
          console.error('Failed to load primitives:', error)
          setPrimitives([])
        } finally {
          setPrimitivesLoaded(true)
        }
      }
      loadPrimitives()
    }
  }, [activeTab, primitivesLoaded])

  // Helper to format batch time for activities
  const formatBatchTime = (timestamp: string | null) => {
    if (!timestamp) return '-'
    const date = new Date(timestamp)
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const hour = String(date.getUTCHours()).padStart(2, '0')
    return `${month}-${day} ${hour}`
  }

  // Helper to format elapsed time
  const formatElapsed = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-'
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${(seconds / 3600).toFixed(1)}h`
  }

  // Reveal a password credential value from the backend
  const handleTogglePasswordVisibility = async (credName: string) => {
    // If already visible, just hide it
    if (visiblePasswords[credName]) {
      setVisiblePasswords(prev => ({ ...prev, [credName]: false }))
      return
    }

    // If value is still the placeholder, fetch the real value from backend
    if (credentialValues[credName] === '***configured***' && integration) {
      setRevealingPassword(prev => ({ ...prev, [credName]: true }))
      try {
        const credentialsPath = orgSlug && orgSlug !== 'integrations'
          ? `/${orgSlug}/integrations/${integration.id}/credentials?reveal=true`
          : `/integrations/${integration.id}/credentials?reveal=true`
        const response = await api.get(credentialsPath)
        if (response.data?.credentials) {
          const revealed = response.data.credentials.find((c: any) => c.name === credName)
          if (revealed && revealed.value && revealed.value !== '***configured***') {
            setCredentialValues(prev => ({ ...prev, [credName]: revealed.value }))
          }
        }
      } catch (error) {
        console.error('Failed to reveal credential:', error)
      } finally {
        setRevealingPassword(prev => ({ ...prev, [credName]: false }))
      }
    }

    setVisiblePasswords(prev => ({ ...prev, [credName]: true }))
  }

  // Auto-save integration name
  const handleSaveName = async () => {
    if (!integration) return
    // Skip if name hasn't changed or already saving
    if (integration.name === originalNameRef.current || savingNameRef.current) return

    savingNameRef.current = true
    try {
      const updated = await integrationsService.update(integration.id!, {
        name: integration.name,
      })
      setIntegration(updated)
      originalNameRef.current = updated.name || ''
      toast.success('Name saved')
    } catch (error: any) {
      console.error('Failed to save name:', error)
      toast.error('Failed to save name')
    } finally {
      savingNameRef.current = false
    }
  }

  // Auto-save enabled state
  const handleSaveEnabled = async (enabled: boolean) => {
    if (!integration) return
    try {
      const updated = await integrationsService.update(integration.id!, {
        enabled: enabled,
      })
      setIntegration(updated)
      toast.success(enabled ? 'Integration enabled' : 'Integration disabled')
    } catch (error: any) {
      console.error('Failed to save enabled state:', error)
      toast.error('Failed to update enabled state')
    }
  }

  const handleSaveTag = async (tag: string | undefined) => {
    if (!integration) return
    try {
      const updated = await integrationsService.update(integration.id!, {
        tag: tag || null,
      } as any)
      // Merge the response with the tag value to ensure it's preserved
      // (in case the API response doesn't include the tag field)
      setIntegration({ ...updated, tag: tag })
      // Add the new tag to availableTags if it's not already there
      if (tag && !availableTags.includes(tag)) {
        setAvailableTags(prev => [...prev, tag].sort())
      }
      toast.success(tag ? `Tag set to "${tag}"` : 'Tag removed')
    } catch (error: any) {
      console.error('Failed to save tag:', error)
      toast.error('Failed to update tag')
    }
  }

  const handleDeleteTag = async (tag: string) => {
    try {
      const result = await integrationsService.deleteTag(tag)
      // Remove the tag from availableTags
      setAvailableTags(prev => prev.filter(t => t !== tag))
      // If the current integration had this tag, clear it
      if (integration?.tag === tag) {
        setIntegration({ ...integration, tag: undefined })
      }
      toast.success(`Tag "${tag}" deleted from ${result.deleted_count} integration(s)`)
    } catch (error: any) {
      console.error('Failed to delete tag:', error)
      toast.error('Failed to delete tag')
    }
  }

  // Get available target primitives from API
  const getAvailablePrimitives = () => {
    return primitives.map(primitive => ({
      value: primitive.slug,
      label: primitive.slug,
      type: primitive.type,
      description: primitive.description
    })).filter(p => p.value).sort((a, b) => a.label.localeCompare(b.label))
  }

  // Get source fields with their current mapping configuration
  const getSourceFields = () => {
    // Only use integration.mapping - no fallback to integrator.mapping
    if (!integration?.mapping || typeof integration.mapping !== 'string') {
      return []
    }

    try {
      const mappingSource = JSON.parse(integration.mapping)

      if (!mappingSource || !mappingSource.mappings) return []

      return Object.entries(mappingSource.mappings)
        .map(([fieldName, config]: [string, any]) => {
          // Handle both string (legacy) and array (new) target formats
          const target = config.target
          let currentTargets: string[] = []
          if (Array.isArray(target)) {
            currentTargets = target.filter((t: string) => t) // Filter out empty strings
          } else if (typeof target === 'string' && target) {
            currentTargets = [target]
          }

          return {
            fieldName,
            currentTargets,
            type: config.type || 'dimension',
            dataType: config.data_type || 'string',
            required: config.required || false
          }
        })
        .sort((a, b) => a.fieldName.localeCompare(b.fieldName))
    } catch (error) {
      console.error('Failed to parse integration mapping:', error)
      return []
    }
  }

  // Handle field mapping change - updates the integration's mapping
  // Now accepts an array of targets to support multiple KPIs per source field
  const handleFieldMappingChange = async (sourceField: string, newTargets: string[]) => {
    if (!integration) return

    // Check if integration has mapping data
    if (!integration.mapping || typeof integration.mapping !== 'string') {
      toast.error('Mapping not found in integration. Please refresh the page.')
      return
    }

    try {
      // Use only integration.mapping
      const mappingData = JSON.parse(integration.mapping)

      // Update the specific field's target
      if (mappingData.mappings && mappingData.mappings[sourceField]) {
        // Store as array to support multiple KPIs - empty array means no mapping
        // Array order is preserved for priority (first = primary KPI)
        mappingData.mappings[sourceField].target = newTargets.length > 0 ? newTargets : []

        // Update timestamps
        mappingData.updated_at = new Date().toISOString()

        // Update stats - count fields with non-empty target arrays as mapped
        const totalFields = Object.keys(mappingData.mappings).length
        const mappedFields = Object.values(mappingData.mappings).filter((config: any) => {
          const target = config.target
          if (Array.isArray(target)) {
            return target.length > 0
          }
          return !!target
        }).length
        mappingData.stats = {
          ...mappingData.stats,
          total_fields: totalFields,
          mapped_fields: mappedFields,
          unmapped_fields: totalFields - mappedFields
        }
      }

      // Update integration mapping
      const updatedIntegration = await integrationsService.update(integration.id!, {
        mapping: JSON.stringify(mappingData)
      })

      setIntegration(updatedIntegration)
      toast.success('Field mapping updated successfully')

    } catch (error) {
      console.error('Failed to update field mapping:', error)
      toast.error('Failed to update field mapping')
    }
  }
  
  const handleTest = async () => {
    if (!integration) return

    // Note: Credential validation is now handled by the API which will:
    // - Check for missing required credentials in the database
    // - Set status to pending_credentials (7) if missing
    // - Set status to credential_error (0) if invalid
    // - Set status to active (1) if valid

    setTesting(true)
    try {
      // First, save any non-empty credentials that were modified
      const updateData: any = {
        name: integration.name,
        enabled: integration.enabled,
      }

      // Include credentials that have actual values, or null to clear
      const credentialsToSave: { [key: string]: string | null } = {}

      credentialDefinitions.forEach(credDef => {
        const name = credDef.name
        if (credDef.input_type === 'oauth_text' || credDef.hidden) {
          return
        }

        const currentValue = credentialValues[name]
        if (currentValue && currentValue !== "***configured***" && currentValue.trim() !== "") {
          // Save actual value (user entered something)
          credentialsToSave[name] = currentValue
        } else if (currentValue === "***configured***") {
          // Password field still showing masked value: don't touch
          return
        } else {
          // Field is empty/cleared: send null to remove value from DB
          credentialsToSave[name] = null
        }
      })

      // Always include credentials to ensure all get saved
      updateData.credentials = credentialsToSave

      // Save credentials before testing (only if there are changes)
      const updated = await integrationsService.update(integration.id!, updateData)
      setIntegration(updated)

      // Now proceed with testing
      const response = await integrationsService.test(integration.id!)

      // Handle the actual API response
      if (response.status === 'success') {
        toast.success(response.message)

        // Refresh integration data to get updated status
        const updatedIntegration = await integrationsService.show(Number(integrationId))
        setIntegration(updatedIntegration)

        // Notify sidebar to refresh credential error count
        window.dispatchEvent(new Event('credentials-updated'))
      } else if (response.status === 'failed') {
        // Show the exact error message from the backend
        const errorMessage = response.message

        toast.error(errorMessage, {
          duration: Infinity,
          style: {
            background: '#ef4444',
            color: 'white',
          },
        })

        // Refresh integration data to get updated status
        const updatedIntegration = await integrationsService.show(Number(integrationId))
        setIntegration(updatedIntegration)

        // Notify sidebar to refresh credential error count
        window.dispatchEvent(new Event('credentials-updated'))
      } else {
        // Fallback for unexpected response format
        toast.success('Test initiated. Check process history for results.')

        // Refresh integration data to get updated status
        const updatedIntegration = await integrationsService.show(Number(integrationId))
        setIntegration(updatedIntegration)

        window.dispatchEvent(new Event('credentials-updated'))
      }
    } catch (error: any) {
      console.error('Test failed:', error)

      // Extract specific error message from API response
      let errorMessage = 'Test failed'
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error?.message) {
        errorMessage = error.message
      }

      toast.error(errorMessage, {
        duration: Infinity,
        style: {
          background: '#ef4444',
          color: 'white',
        },
      })

      // Refresh integration data to get updated status
      try {
        const updatedIntegration = await integrationsService.show(Number(integrationId))
        setIntegration(updatedIntegration)
      } catch {
        // Ignore refresh errors
      }

      // Notify sidebar to refresh credential error count
      window.dispatchEvent(new Event('credentials-updated'))
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    if (!integration) return
    
    try {
      await integrationsService.delete(integration.id!)
      toast.success('Integration deleted successfully')
      navigate(`/${orgSlug}/integrations`)
    } catch (error) {
      console.error('Failed to delete integration:', error)
      toast.error('Failed to delete integration')
    }
  }
  
  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleBackfill = async () => {
    if (!integration || !backfillDateRange?.from || !backfillDateRange?.to) return

    // Close dialog immediately to prevent double-clicks
    setBackfillDialogOpen(false)

    const startDateStr = formatDateForApi(backfillDateRange.from)
    const endDateStr = formatDateForApi(backfillDateRange.to)

    try {
      const response = await integrationsService.backfill(integration.id!, startDateStr, endDateStr)

      // Capture job ID and start polling
      // API returns: { status, message, job_id, integration_id, date_range, total_days }
      if (response.job_id) {
        // Set active backfill to disable button
        setActiveBackfill({
          job_id: response.job_id,
          integration_id: integration.id!,
          status: 'pending',
          period_start: endDateStr,
          period_end: startDateStr,
          created_at: new Date().toISOString(),
          total_periods: response.total_days || 0,
          completed_periods: 0,
          failed_periods: 0,
          progress_pct: 0
        })
        setBackfillJobIds([response.job_id])
        setBackfillProgress({
          job_id: response.job_id,
          integration_id: integration.id!,
          integration_name: integration.name,
          organization_id: 0,
          organization_name: '',
          current_step: 'collect',
          status: 'queued',
          progress_pct: 0,
          records_processed: 0,
          eta_seconds: 0,
          started_at: new Date().toISOString(),
          periods_completed: 0,
          total_periods: response.total_days || 0,
          periods_failed: 0,
          period_start: startDateStr,
          period_end: endDateStr
        })
        setBackfillPollingActive(true)
        toast.success(`Backfill started for ${response.total_days || 'multiple'} days`)
      } else {
        toast.success(response.message || 'Backfill initiated successfully')
      }

      setBackfillDateRange(undefined)
    } catch (error: any) {
      console.error('Backfill failed:', error)
      // Check if it's a conflict error (active backfill already exists)
      if (error?.response?.status === 409) {
        const detail = error.response?.data?.detail || 'A backfill is already in progress for this integration'
        toast.error(detail)
        // Refresh to get the active backfill status
        loadData()
      } else {
        toast.error('Backfill failed')
      }
    }
  }

  const handleCancelBackfill = async () => {
    if (!integration) return

    setCancellingBackfill(true)
    try {
      const response = await integrationsService.cancelBackfill(integration.id!)
      if (response.status === 'success') {
        toast.success('Backfill cancelled successfully')
        // Clear backfill progress state
        setBackfillProgress(null)
        setBackfillJobIds([])
        setBackfillPollingActive(false)
        setActiveBackfill(null)
      } else {
        toast.error(response.message || 'Failed to cancel backfill')
      }
    } catch (error: any) {
      console.error('Cancel backfill failed:', error)
      // 404 means no active backfill - treat as success (already completed)
      if (error?.response?.status === 404) {
        toast.success('Backfill already completed')
        setBackfillProgress(null)
        setBackfillJobIds([])
        setBackfillPollingActive(false)
        setActiveBackfill(null)
      } else {
        const message = error?.response?.data?.detail || 'Failed to cancel backfill'
        toast.error(message)
      }
    } finally {
      setCancellingBackfill(false)
      setCancelBackfillDialogOpen(false)
    }
  }

  const handleBackfillCloseClick = () => {
    // If backfill is still running/queued, show confirmation dialog
    const isInProgress = backfillProgress?.status === 'running' || backfillProgress?.status === 'queued'
    if (isInProgress) {
      setCancelBackfillDialogOpen(true)
    } else {
      // If finished (success/failed), just dismiss the progress card
      setBackfillProgress(null)
      setBackfillJobIds([])
    }
  }

  const handleRevokeAuthorization = async () => {
    if (!selectedAuthorizationId || selectedAuthorizationId === 'create_new') return

    setRevokingAuth(true)
    try {
      await api.delete(`/oauth/authorizations/${selectedAuthorizationId}`)
      toast.success('Authorization revoked successfully')

      // Clear the selected authorization and refresh the list
      setSelectedAuthorizationId('')
      await fetchAuthorizations()

      // Reload integration data to update status
      await loadData()
    } catch (error: any) {
      console.error('Failed to revoke authorization:', error)
      const message = error?.response?.data?.detail || 'Failed to revoke authorization'
      toast.error(message)
    } finally {
      setRevokingAuth(false)
      setRevokeAuthDialogOpen(false)
    }
  }

  const handleAuthorizationChange = async (authorizationId: string) => {
    if (!integration) return

    try {
      if (authorizationId === 'create_new') {
        // Check if there are existing authorizations that would be revoked
        if (authorizations.length > 0) {
          // Show confirmation dialog first
          setRevokeConfirmDialogOpen(true)
        } else {
          // No existing authorizations, proceed directly
          openOAuthPopup(integration.id!, integrator!.slug)
        }
      } else {
        // Update integration with selected authorization
        const updatedIntegration = await integrationsService.update(integration.id!, {
          authorization_id: parseInt(authorizationId)
        })
        setIntegration(updatedIntegration)
        setSelectedAuthorizationId(authorizationId)
        toast.success('Authorization updated successfully')
      }
    } catch (error) {
      console.error('Failed to update authorization:', error)
      toast.error('Failed to update authorization')
    }
  }

  const handleConfirmedRevoke = async () => {
    setRevokeConfirmDialogOpen(false)
    await revokeExistingAuthorizationsAndCreateNew()
  }

  const revokeExistingAuthorizationsAndCreateNew = async () => {
    if (!integration || !integrator) return

    try {
      console.log('🔍 Integration data:', {
        id: integration.id,
        authorization_id: integration.authorization_id,
        integrator_id: integration.integrator_id
      })
      console.log('🔍 Authorizations found:', authorizations.length, authorizations)
      
      // Check if there are existing authorizations that need to be revoked
      if (authorizations.length > 0) {
        console.log('Revoking existing authorizations before creating new one')
        toast.info('Cleaning up existing authorizations...')
        
        // Revoke existing authorizations for this integrator to ensure clean OAuth flow
        try {
          console.log('Calling revoke API:', `/oauth/integrations/${integration.id}/revoke`)
          const revokeResponse = await api.post(`/oauth/integrations/${integration.id}/revoke`)
          console.log('✅ Revoke API response:', revokeResponse.status, revokeResponse.data)
          toast.success('Existing authorizations cleaned up successfully')
        } catch (revokeError: any) {
          console.error('❌ Failed to revoke existing authorizations:', revokeError)
          console.error('❌ Revoke error details:', {
            status: revokeError?.response?.status,
            data: revokeError?.response?.data,
            message: revokeError?.message
          })
          toast.error(`Failed to clean up authorizations: ${revokeError?.response?.data?.detail || revokeError?.message}`)
          return // Don't proceed if revoke fails
        }
        
        // Small delay to ensure revocation is processed
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      
      // Now open OAuth popup for new authorization
      console.log('Starting OAuth flow for new authorization')
      toast.info('Starting authorization process...')
      openOAuthPopup(integration.id!, integrator!.slug)
      
    } catch (error) {
      console.error('Failed to revoke existing authorizations:', error)
      toast.error('Failed to prepare for new authorization')
    }
  }

  const openOAuthPopup = (integrationId: number, integratorSlug: string) => {
    const width = 562
    const height = 720
    const left = (screen.width/2)-(width/2)
    const top = (screen.height/2)-(height/2)
    
    const popup = window.open(
      `/${orgSlug}/oauth_integration?integration_id=${integrationId}&slug=${integratorSlug}`,
      '_blank',
      `toolbar=no,scrollbars=no,resizable=no,top=${top},left=${left},width=${width},height=${height}`
    )
    
    // Listen for OAuth completion message from popup
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      
      if (event.data.type === 'OAUTH_SUCCESS' && event.data.data?.integrationId === integrationId) {
        console.log('OAuth completed successfully, refreshing authorization data')
        window.removeEventListener('message', handleMessage)
        clearInterval(checkClosed)
        
        // Refetch authorizations and update the integration
        fetchAuthorizations().then(() => {
          // Also reload integration data to get updated authorization_id
          loadData()
          toast.success('Authorization created successfully')
        })
      } else if (event.data.type === 'OAUTH_ERROR') {
        console.error('OAuth failed:', event.data.data?.message)
        window.removeEventListener('message', handleMessage)
        clearInterval(checkClosed)
        toast.error(event.data.data?.message || 'OAuth authorization failed')
      }
    }
    
    window.addEventListener('message', handleMessage)
    
    // Fallback: Poll to detect when popup closes (for browsers that support it)
    const checkClosed = setInterval(() => {
      try {
        if (popup?.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          console.log('OAuth popup closed via polling, refreshing authorization data')
          
          // Small delay to ensure OAuth process completes on server
          setTimeout(() => {
            fetchAuthorizations().then(() => {
              loadData()
            })
          }, 1000)
        }
      } catch (error) {
        // Cross-Origin-Opener-Policy blocks window.closed access
        console.log('Cross-Origin-Opener-Policy policy would block the window.closed call.')
      }
    }, 1000) // Check every second
    
    // Cleanup if popup doesn't close within 10 minutes
    setTimeout(() => {
      clearInterval(checkClosed)
      window.removeEventListener('message', handleMessage)
      console.log('OAuth popup polling timeout')
    }, 600000) // 10 minutes timeout
  }
  
  // @ts-ignore - Function kept for future status display
  const _getStatusIcon = () => {
    if (!integration) return null
    
    // Handle both string and integer status values
    const statusValue = typeof integration.status === 'number' 
      ? getStatusNameFromValue(integration.status)
      : integration.status

    switch (statusValue) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'credential_error':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'pending_credentials':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'oauth_stub':
        return <AlertCircle className="h-4 w-4 text-blue-500" />
      case 'verifying':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
      case 'revoked':
        return <XCircle className="h-4 w-4 text-gray-500" />
      case 'no_content':
        return <AlertCircle className="h-4 w-4 text-orange-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />
    }
  }
  
  // @ts-ignore - Function kept for future status display
  const _getStatusText = () => {
    if (!integration) return 'Unknown'
    
    // Handle both string and integer status values (same as integrations list page)
    const statusValue = typeof integration.status === 'number' 
      ? getStatusNameFromValue(integration.status)
      : integration.status

    // If no valid status, return unknown
    if (!statusValue) {
      return 'Unknown'
    }

    switch (statusValue) {
      case 'active':
        return 'Active'
      case 'credential_error':
        return 'Invalid Credentials'
      case 'error':
        return 'Error'
      case 'pending_credentials':
        return 'Pending Credentials'
      case 'no_content':
        return 'No Content'
      case 'oauth_stub':
        return 'OAuth Pending'
      case 'verifying':
        return 'Verifying'
      case 'revoked':
        return 'Revoked'
      default:
        return statusValue || 'Unknown'
    }
  }

  const getStatusBadge = () => {
    if (!integration) return null

    // Check enabled first (matching list page behavior)
    if (!integration.enabled) {
      return <Badge variant="secondary">Disabled</Badge>
    }

    let variant: 'default' | 'destructive' | 'secondary' | 'outline' = 'default'
    let label = ''

    // Handle both string and integer status values
    const statusValue = typeof integration.status === 'number'
      ? getStatusNameFromValue(integration.status)
      : integration.status

    // If no valid status, return empty badge
    if (!statusValue) {
      return null
    }

    switch (statusValue) {
      case 'credential_error':
        variant = 'destructive'
        label = 'Invalid Credentials'
        break
      case 'active':
        variant = 'default'
        label = 'Active'
        break
      case 'no_content':
        variant = 'secondary'
        label = 'No Content'
        break
      case 'error':
        variant = 'destructive'
        label = 'Error'
        break
      case 'pending_credentials':
        variant = 'outline'
        label = 'Pending Credentials'
        break
    }

    // Check for process errors
    if (integration.last_process_status === 2 && integration.enabled) {
      variant = 'destructive'
      label = 'Error'
    }

    return <Badge variant={variant}>{label}</Badge>
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }
  
  if (!integration) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold">Integration not found</h2>
        <Button
          className="mt-4"
          onClick={() => navigate(`/${orgSlug}/integrations`)}
        >
          Back to Integrations
        </Button>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Edit Integration</h1>
          <p className="text-muted-foreground">Manage your integration settings</p>
        </div>
      </div>

      {/* Backfill Progress Card - Only shows when backfill is active */}
      {backfillProgress && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {backfillProgress.status === 'running' || backfillProgress.status === 'queued' ? (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  ) : backfillProgress.status === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className="font-medium">
                    Backfill {backfillProgress.status === 'success' ? 'Completed' :
                             backfillProgress.status === 'failed' ? 'Failed' :
                             backfillProgress.status === 'queued' ? 'Queued' : 'In Progress'}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {backfillProgress.period_start && backfillProgress.period_end && (
                    <span>
                      {new Date(backfillProgress.period_start).toLocaleDateString(undefined, { timeZone: 'UTC' })} → {new Date(backfillProgress.period_end).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                    </span>
                  )}
                  {backfillProgress.periods_completed !== undefined && backfillProgress.total_periods !== undefined && (
                    <span>
                      {(() => {
                        // Calculate correct days from date range if available
                        const calculatedDays = activeBackfill
                          ? calculateDaysFromRange(activeBackfill.period_start, activeBackfill.period_end)
                          : null
                        const totalDays = calculatedDays || backfillProgress.total_periods
                        // Scale completed proportionally if we calculated different total
                        const completedDays = calculatedDays && backfillProgress.total_periods > 0
                          ? Math.round((backfillProgress.periods_completed / backfillProgress.total_periods) * totalDays)
                          : backfillProgress.periods_completed
                        return `${completedDays} / ${totalDays} periods`
                      })()}
                    </span>
                  )}
                  {backfillProgress.records_processed !== undefined && (
                    <span>{backfillProgress.records_processed.toLocaleString()} records</span>
                  )}
                  {backfillProgress.eta_seconds > 0 && backfillProgress.status === 'running' && (
                    <span>ETA: {Math.ceil(backfillProgress.eta_seconds / 60)} min</span>
                  )}
                  <Badge variant={
                    backfillProgress.status === 'success' ? 'default' :
                    backfillProgress.status === 'failed' ? 'destructive' :
                    'secondary'
                  }>
                    {backfillProgress.current_step}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleBackfillCloseClick}
                    title={backfillProgress.status === 'running' || backfillProgress.status === 'queued' ? 'Cancel backfill' : 'Dismiss'}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Progress
                value={backfillProgress.progress_pct}
                className={`h-2 ${
                  backfillProgress.status === 'success' ? '[&>div]:bg-green-600' :
                  backfillProgress.status === 'failed' ? '[&>div]:bg-red-600' :
                  '[&>div]:bg-blue-600'
                }`}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{Math.round(backfillProgress.progress_pct)}% complete</span>
                {backfillProgress.started_at && (
                  <span>Started: {new Date(backfillProgress.started_at).toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {/* Integration Info Card - Responsive Layout */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
              {/* Left side - Logo and Name */}
              <div className="flex items-start gap-4 min-w-0">
                {integrator && (
                  <img
                    src={`https://storage.googleapis.com/integrator-guide/logos/${integrator.slug}.png`}
                    alt={integrator.name}
                    className="h-16 w-16 rounded-lg object-contain flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                )}
                <div className="space-y-1 min-w-0 flex-1">
                  <Input
                    value={integration.name}
                    onChange={(e) => setIntegration({ ...integration, name: e.target.value })}
                    onBlur={handleSaveName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur()
                      }
                    }}
                    className="text-xl font-semibold h-auto py-1 px-0 border-transparent hover:border-input focus:border-input focus:px-2 bg-transparent w-full"
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                      <p className="text-sm text-muted-foreground">{integrator?.slug} · {integrator?.segment} · {integration.id}|{slugifyName(integration.name)}</p>
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                navigator.clipboard.writeText(`${integration.id}|${slugifyName(integration.name)}`)
                                toast.success('Copied to clipboard')
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Use this code for Normalization conditionals
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {integration.tag && <span className="text-sm text-muted-foreground"> · {integration.tag}</span>}
                    </div>
                </div>
              </div>

              {/* Right side - Status badge and options menu */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {getStatusBadge()}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Integration Settings</DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {/* Enabled Toggle */}
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      className="flex items-center justify-between"
                    >
                      <span>Enabled</span>
                      <Switch
                        checked={integration.enabled}
                        onCheckedChange={(checked) => {
                          setIntegration({ ...integration, enabled: checked })
                          handleSaveEnabled(checked)
                        }}
                        className="scale-75"
                      />
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Backfill */}
                    <DropdownMenuItem
                      onSelect={() => setBackfillDialogOpen(true)}
                      disabled={!integration || activeBackfill !== null || (
                        typeof integration.status === 'number'
                          ? !['active', 'error', 'no_content'].includes(getStatusNameFromValue(integration.status))
                          : !['active', 'error', 'no_content'].includes(integration.status || '')
                      )}
                    >
                      <History className="mr-2 h-4 w-4" />
                      {activeBackfill ? 'Backfill in Progress' : 'Backfill Data'}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Tag */}
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Tag</DropdownMenuLabel>
                    <div className="px-2 pb-2">
                      <Combobox
                        value={integration.tag || ''}
                        onValueChange={(value) => {
                          setIntegration({ ...integration, tag: value || undefined })
                          handleSaveTag(value || undefined)
                        }}
                        onDeleteOption={handleDeleteTag}
                        placeholder="Select tag..."
                        searchPlaceholder="Search or add tag..."
                        emptyText="Type to add new tag"
                        options={availableTags.map(tag => ({
                          value: tag,
                          label: tag
                        }))}
                        allowCustomValue={true}
                        className="w-full"
                        deleteConfirmTitle="Delete this tag?"
                        deleteConfirmDescription="This will remove the tag '{option}' from all integrations that use it. This action cannot be undone."
                      />
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Backfill Dialog */}
                <Dialog open={backfillDialogOpen} onOpenChange={setBackfillDialogOpen}>
                  <DialogContent className={isMobile ? "max-w-[95vw]" : "max-w-fit"}>
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        Backfill Data
                        {backfillDateRange?.from && backfillDateRange?.to && (
                          <Badge variant="secondary">
                            {Math.ceil((backfillDateRange.to.getTime() - backfillDateRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                          </Badge>
                        )}
                      </DialogTitle>
                      <DialogDescription>
                        {isMobile ? 'Select date range for backfill.' : 'Click to select range. Double-click to reset.'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      {isMobile ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="backfill-from">From</Label>
                            <Input
                              id="backfill-from"
                              type="date"
                              max={(() => {
                                const yesterday = new Date()
                                yesterday.setDate(yesterday.getDate() - 1)
                                return yesterday.toISOString().split('T')[0]
                              })()}
                              value={backfillDateRange?.from ? backfillDateRange.from.toISOString().split('T')[0] : ''}
                              onChange={(e) => {
                                const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined
                                setBackfillDateRange(prev => ({ ...prev, from: date, to: prev?.to }))
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="backfill-to">To</Label>
                            <Input
                              id="backfill-to"
                              type="date"
                              max={(() => {
                                const yesterday = new Date()
                                yesterday.setDate(yesterday.getDate() - 1)
                                return yesterday.toISOString().split('T')[0]
                              })()}
                              value={backfillDateRange?.to ? backfillDateRange.to.toISOString().split('T')[0] : ''}
                              onChange={(e) => {
                                const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : undefined
                                setBackfillDateRange(prev => ({ ...prev, from: prev?.from, to: date }))
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <CalendarComponent
                          mode="range"
                          defaultMonth={(() => {
                            // Show previous month on left, current month on right
                            const lastMonth = new Date()
                            lastMonth.setMonth(lastMonth.getMonth() - 1)
                            return backfillDateRange?.from || lastMonth
                          })()}
                          selected={backfillDateRange}
                          onSelect={setBackfillDateRange}
                          numberOfMonths={2}
                          showOutsideDays={false}
                          disabled={{ after: (() => {
                            const yesterday = new Date()
                            yesterday.setDate(yesterday.getDate() - 1)
                            return yesterday
                          })() }}
                          className="rounded-lg border shadow-sm"
                        />
                      )}
                      {!isMobile && backfillDateRange?.from && backfillDateRange?.to && (
                        <div className="mt-4 text-sm text-muted-foreground text-center">
                          {backfillDateRange.from.toLocaleDateString()} - {backfillDateRange.to.toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setBackfillDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleBackfill}
                        disabled={!backfillDateRange?.from || !backfillDateRange?.to}
                      >
                        Start Backfill
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs Card */}
          <Card>
            <CardContent className="pt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                  <TabsList>
                    <TabsTrigger value="credentials">
                      <Lock className="mr-2 h-4 w-4" />
                      Credentials
                    </TabsTrigger>
                    <TabsTrigger value="mapping">
                      <ArrowRightLeft className="mr-2 h-4 w-4" />
                      Mapping
                    </TabsTrigger>
                    <TabsTrigger value="activities">
                      <Activity className="mr-2 h-4 w-4" />
                      Activities
                    </TabsTrigger>
                    <TabsTrigger value="danger">
                      <AlertTriangle className="mr-2 h-4 w-4" />
                      Danger Zone
                    </TabsTrigger>
                  </TabsList>
                  <div className="self-end sm:self-auto">
                    {activeTab === 'credentials' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleTest}
                        disabled={testing}
                      >
                        {testing ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            <span className="hidden lg:inline">Testing...</span>
                            <span className="lg:hidden">Test</span>
                          </>
                        ) : (
                          <>
                            <PlayCircle className="mr-2 h-4 w-4" />
                            <span className="hidden lg:inline">Test Credentials</span>
                            <span className="lg:hidden">Test</span>
                          </>
                        )}
                      </Button>
                    )}
                    {activeTab === 'mapping' && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Database className="h-3 w-3" />
                        {getSourceFields().length} fields
                      </Badge>
                    )}
                    {activeTab === 'activities' && (
                      <div className="flex items-center gap-3">
                        {collectionStats && (
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Database className="h-3 w-3" />
                            {collectionStats.recs.toLocaleString()} records
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-muted-foreground">
                          {jobsTotal} jobs (last 7 days)
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={fetchJobs}
                          disabled={jobsLoading}
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${jobsLoading ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <TabsContent value="credentials" className="mt-0">
                  <Card className="overflow-hidden">
                      <CardContent className="p-0">
                        <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
                        <div className="overflow-x-auto">
                        <Table className="table-fixed min-w-[500px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[20%]">
                                Credential <span className="text-xs font-normal text-muted-foreground">(<Asterisk className="h-3 w-3 text-destructive inline" /> required)</span>
                              </TableHead>
                              <TableHead className="w-[40%]">Value</TableHead>
                              <TableHead className="w-[40%]">Tip</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {/* Authorization row for OAuth2 integrators */}
                            {integrator?.technology === 'api_oauth2' && (
                              <TableRow>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-1">
                                    Authorization
                                    <Asterisk className="h-3 w-3 text-destructive" />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Combobox
                                      value={selectedAuthorizationId}
                                      onValueChange={handleAuthorizationChange}
                                      disabled={loadingAuthorizations}
                                      placeholder={loadingAuthorizations ? "Loading..." : "Select or create authorization..."}
                                      searchPlaceholder="Search authorizations..."
                                      emptyText="No authorization found."
                                      options={[
                                        {
                                          value: "create_new",
                                          label: "Create new authorization",
                                          icon: <Plus className="h-4 w-4" />
                                        },
                                        ...authorizations.map((auth): ComboboxOption => ({
                                          value: auth.id.toString(),
                                          label: auth.name || `Authorization ${auth.id}`,
                                          description: `Created: ${new Date(auth.created_at).toLocaleDateString()}`
                                        }))
                                      ]}
                                    />
                                    {selectedAuthorizationId && selectedAuthorizationId !== 'create_new' && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => setRevokeAuthDialogOpen(true)}
                                        title="Revoke this authorization"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="text-xs text-muted-foreground">
                                    OAuth2 authorization for this integration
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {credentialDefinitions.filter(credDef => !credDef.hidden).map((credDef) => {
                              const isRequired = !credDef.optional
                              return (
                                <TableRow key={credDef.id}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-1">
                                      {credDef.label || credDef.name}
                                      {isRequired && <Asterisk className="h-3 w-3 text-destructive" />}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      {/* Special handling for AdMob report_type credential */}
                                      {credDef.name === 'report_type' && integrator?.id === 151 ? (
                                        <Select
                                          value={credentialValues[credDef.name] || 'network'}
                                          onValueChange={(value) => {
                                            setCredentialValues(prev => ({
                                              ...prev,
                                              [credDef.name]: value
                                            }))
                                          }}
                                        >
                                          <SelectTrigger className="w-full max-w-[800px]">
                                            <SelectValue placeholder="Select report type..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="network">
                                              <div className="flex flex-col">
                                                <span>Network Report (Recommended)</span>
                                                <span className="text-xs text-muted-foreground">Includes AD_UNIT breakdown. Simpler, fewer rows.</span>
                                              </div>
                                            </SelectItem>
                                            <SelectItem value="mediation">
                                              <div className="flex flex-col">
                                                <span>Mediation Report</span>
                                                <span className="text-xs text-muted-foreground">Includes AD_SOURCE for ad network breakdown. No AD_UNIT.</span>
                                              </div>
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : credDef.input_type === 'password' ? (
                                        <>
                                          <Input
                                            type={visiblePasswords[credDef.name] ? 'text' : 'password'}
                                            value={credentialValues[credDef.name] === "***configured***" ? '**************' : (credentialValues[credDef.name] || '')}
                                            onFocus={() => {
                                              if (credentialValues[credDef.name] === "***configured***") {
                                                setCredentialValues(prev => ({
                                                  ...prev,
                                                  [credDef.name]: ''
                                                }))
                                              }
                                            }}
                                            onChange={(e) => {
                                              setCredentialValues(prev => ({
                                                ...prev,
                                                [credDef.name]: e.target.value
                                              }))
                                            }}
                                            className="w-full max-w-[800px]"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => handleTogglePasswordVisibility(credDef.name)}
                                            className="p-1 text-muted-foreground hover:text-foreground"
                                            tabIndex={-1}
                                            title={visiblePasswords[credDef.name] ? 'Hide' : 'Show'}
                                            disabled={revealingPassword[credDef.name]}
                                          >
                                            {revealingPassword[credDef.name] ? (
                                              <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : visiblePasswords[credDef.name] ? (
                                              <EyeOff className="h-4 w-4" />
                                            ) : (
                                              <Eye className="h-4 w-4" />
                                            )}
                                          </button>
                                        </>
                                      ) : credDef.input_type === 'text' ? (
                                        <Input
                                          type="text"
                                          value={credentialValues[credDef.name] || ''}
                                          placeholder={credDef.placeholder || ''}
                                          onChange={(e) => {
                                            setCredentialValues(prev => ({
                                              ...prev,
                                              [credDef.name]: e.target.value
                                            }))
                                          }}
                                          className="w-full max-w-[800px]"
                                        />
                                      ) : credDef.input_type === 'select' && credDef.selectable_options ? (
                                        <Select
                                          value={credentialValues[credDef.name] || ''}
                                          onValueChange={(value) => {
                                            setCredentialValues(prev => ({
                                              ...prev,
                                              [credDef.name]: value
                                            }))
                                          }}
                                        >
                                          <SelectTrigger className="w-full max-w-[800px]">
                                            <SelectValue placeholder="Select option..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Object.entries(credDef.selectable_options).map(([key, label]) => (
                                              <SelectItem key={key} value={key}>
                                                {label as string}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : credDef.input_type === 'boolean' || credDef.input_type === 'checkbox' || ['true', 'false'].includes((credentialValues[credDef.name] || '').toLowerCase()) ? (
                                        <div className="flex items-center gap-3">
                                          <Switch
                                            checked={(credentialValues[credDef.name] || '').toLowerCase() === 'true'}
                                            onCheckedChange={(checked) => {
                                              setCredentialValues(prev => ({
                                                ...prev,
                                                [credDef.name]: checked ? 'true' : 'false'
                                              }))
                                            }}
                                          />
                                          <span className="text-sm text-muted-foreground">
                                            {(credentialValues[credDef.name] || '').toLowerCase() === 'true' ? 'Enabled' : 'Disabled'}
                                          </span>
                                        </div>
                                      ) : credDef.input_type === 'oauth2' ? (
                                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                          OAuth2 (handled separately)
                                        </Badge>
                                      ) : (
                                        <Input
                                          value={credentialValues[credDef.name] || ''}
                                          placeholder={credDef.placeholder || ''}
                                          onChange={(e) => {
                                            setCredentialValues(prev => ({
                                              ...prev,
                                              [credDef.name]: e.target.value
                                            }))
                                          }}
                                          className="w-full max-w-[800px]"
                                        />
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {credDef.tip && (
                                      <div
                                        className="text-xs text-muted-foreground prose prose-xs max-w-none"
                                        dangerouslySetInnerHTML={{ __html: credDef.tip }}
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                        </div>

                        {credentialDefinitions.filter(c => !c.hidden).length === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                            <p>No credentials configured</p>
                            <p className="text-sm mt-2">This integration does not require credentials.</p>
                          </div>
                        )}
                        </form>
                      </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="mapping" className="mt-0">
                  <Card className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                        <Table className="table-fixed min-w-[500px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[40%]">Source Field</TableHead>
                              <TableHead className="w-[60%]">Mapped To</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {!primitivesLoaded ? (
                              <TableRow>
                                <TableCell colSpan={2} className="text-center py-12">
                                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">Loading mapping...</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              getSourceFields().map((field) => (
                                <TableRow key={field.fieldName}>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-1">
                                      {field.fieldName} <span className="text-muted-foreground font-normal">({field.dataType})</span>
                                      {field.required && <Asterisk className="h-3 w-3 text-destructive" />}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <OrderedMultiSelect
                                        options={getAvailablePrimitives()}
                                        value={field.currentTargets}
                                        onValueChange={(values) => handleFieldMappingChange(field.fieldName, values)}
                                        placeholder="Select primitives..."
                                        className="w-[280px]"
                                      />
                                      <span className="text-sm text-muted-foreground">{field.type}</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                        </div>

                        {primitivesLoaded && getSourceFields().length === 0 && (
                          <div className="text-center py-8 text-muted-foreground">
                            <Database className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                            <p>No mapping data available</p>
                            <p className="text-sm mt-2">The integrator does not have field mapping configured.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="activities" className="mt-0">
                  <Card className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Batch</TableHead>
                              <TableHead>Job ID</TableHead>
                              <TableHead>Collection</TableHead>
                              <TableHead>
                                <div className="flex items-center gap-1">
                                  Status
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-6 px-1 ${jobStatusFilter.size < 4 ? 'bg-primary/10 text-primary' : ''}`}
                                      >
                                        <Filter className="h-3 w-3" />
                                        {jobStatusFilter.size < 4 && (
                                          <span className="ml-1 text-xs">{jobStatusFilter.size}</span>
                                        )}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-44 p-2" align="start">
                                      <div className="space-y-2">
                                        {[
                                          { value: 'completed', label: 'Completed' },
                                          { value: 'failed', label: 'Failed' },
                                          { value: 'running', label: 'Running' },
                                          { value: 'queued', label: 'Queued' },
                                        ].map(({ value, label }) => (
                                          <div key={value} className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`job-status-${value}`}
                                              checked={jobStatusFilter.has(value)}
                                              onCheckedChange={(checked) => {
                                                const newStatuses = new Set(jobStatusFilter)
                                                if (checked) {
                                                  newStatuses.add(value)
                                                } else {
                                                  newStatuses.delete(value)
                                                }
                                                // Ensure at least one status is selected
                                                if (newStatuses.size > 0) {
                                                  setJobStatusFilter(newStatuses)
                                                }
                                              }}
                                            />
                                            <Label htmlFor={`job-status-${value}`} className="text-sm cursor-pointer">
                                              {label}
                                            </Label>
                                          </div>
                                        ))}
                                        <div className="border-t pt-2 mt-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full h-7 text-xs"
                                            onClick={() => setJobStatusFilter(new Set(['queued', 'running', 'completed', 'failed']))}
                                          >
                                            Select All
                                          </Button>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </TableHead>
                              <TableHead>Progress</TableHead>
                              <TableHead className="text-right" title="Records processed">Recs</TableHead>
                              <TableHead className="text-right">Duration</TableHead>
                              <TableHead>Error</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {jobsLoading && jobs.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} className="text-center py-12">
                                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">Loading jobs...</p>
                                </TableCell>
                              </TableRow>
                            ) : jobs.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} className="text-center py-12">
                                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500/50" />
                                  <p className="text-muted-foreground">No jobs in the last 7 days</p>
                                  <p className="text-sm text-muted-foreground mt-2">This integration has no recent activity</p>
                                </TableCell>
                              </TableRow>
                            ) : (
                              jobs.map((job) => {
                                // Get status color
                                const getStatusStyle = () => {
                                  switch (job.status) {
                                    case 'failed': return { label: 'Failed', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
                                    case 'running': return { label: 'Running', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' }
                                    case 'queued': return { label: 'Queued', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400' }
                                    case 'completed': return { label: 'Completed', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' }
                                    default: return { label: job.status, color: 'bg-slate-100 text-slate-700' }
                                  }
                                }
                                const statusStyle = getStatusStyle()

                                // Calculate duration (all timestamps are UTC)
                                const calculateDuration = () => {
                                  if (!job.started_at) return null
                                  // Ensure timestamps are parsed as UTC by appending Z if no timezone info
                                  const parseUtc = (ts: string) => {
                                    if (!ts.endsWith('Z') && !ts.includes('+') && !ts.includes('-', 10)) {
                                      return new Date(ts + 'Z').getTime()
                                    }
                                    return new Date(ts).getTime()
                                  }
                                  const start = parseUtc(job.started_at)
                                  const end = job.completed_at ? parseUtc(job.completed_at) : Date.now()
                                  return Math.floor((end - start) / 1000)
                                }
                                const durationSeconds = calculateDuration()

                                // Format batch timestamp as MMDDHH (UTC)
                                const formatBatch = (timestamp: string | null | undefined) => {
                                  if (!timestamp) return '-'
                                  // Parse as UTC - append Z if no timezone info present
                                  let dateStr = timestamp
                                  if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
                                    dateStr = timestamp + 'Z'
                                  }
                                  const date = new Date(dateStr)
                                  if (isNaN(date.getTime())) return '-'
                                  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
                                  const day = String(date.getUTCDate()).padStart(2, '0')
                                  const hour = String(date.getUTCHours()).padStart(2, '0')
                                  return `${month}${day}${hour}`
                                }

                                return (
                                  <TableRow key={job.job_id}>
                                    <TableCell className="font-mono text-xs whitespace-nowrap">
                                      {formatBatch(job.started_at || job.created_at)}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {job.job_id}
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-xs capitalize text-muted-foreground">
                                        {job.kind || '-'}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="secondary" className={`text-xs ${statusStyle.color}`}>
                                        {statusStyle.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <JobProgressBar
                                        progressPct={job.progress_pct}
                                        periodsCompleted={job.periods_completed}
                                        totalPeriods={job.total_periods}
                                        periodsFailed={job.periods_failed}
                                        periodStart={job.period_start}
                                        periodEnd={job.period_end}
                                      />
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-xs">
                                      {job.records_processed > 0 ? job.records_processed.toLocaleString() : '-'}
                                    </TableCell>
                                    <TableCell className="text-right font-mono text-xs">
                                      {formatElapsed(durationSeconds)}
                                    </TableCell>
                                    <TableCell>
                                      {job.error_message ? (
                                        <span
                                          className="text-xs text-red-600 dark:text-red-400 truncate block max-w-[250px] cursor-help"
                                          title={job.error_message}
                                        >
                                          {job.error_message}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="danger" className="mt-0">
                  <Card className="border-destructive/50">
                    <CardHeader>
                      <CardTitle className="text-destructive">
                        Danger Zone
                      </CardTitle>
                      <CardDescription>
                        Irreversible and destructive actions
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Integration
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Delete Integration</DialogTitle>
                            <DialogDescription>
                              Are you sure you want to delete this integration? This action cannot be undone.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleDelete}>
                              Delete
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
      </div>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeConfirmDialogOpen} onOpenChange={setRevokeConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Authorization</DialogTitle>
            <DialogDescription>
              You have existing authorization(s) for {integrator?.name || 'this integrator'}. To create a new authorization,
              the existing one(s) will be revoked to ensure proper token refresh capabilities.
              <br /><br />
              <strong>This will:</strong>
              <ul className="list-disc ml-4 mt-2">
                <li>Revoke existing authorization(s)</li>
                <li>Open {integrator?.name || 'OAuth'} consent screen</li>
                <li>Create fresh authorization with proper permissions</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmedRevoke}>
              Continue with New Authorization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Backfill Confirmation Dialog */}
      <Dialog open={cancelBackfillDialogOpen} onOpenChange={setCancelBackfillDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Backfill</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this backfill? The backfill is still in progress.
              <br /><br />
              {backfillProgress && (
                <span className="text-sm">
                  Progress: {(() => {
                    const calculatedDays = activeBackfill
                      ? calculateDaysFromRange(activeBackfill.period_start, activeBackfill.period_end)
                      : null
                    const backfillTotal = backfillProgress.total_periods ?? 0
                    const backfillCompleted = backfillProgress.periods_completed ?? 0
                    const totalDays = calculatedDays || backfillTotal
                    const completedDays = calculatedDays && backfillTotal > 0
                      ? Math.round((backfillCompleted / backfillTotal) * totalDays)
                      : backfillCompleted
                    return `${completedDays} / ${totalDays}`
                  })()} periods ({Math.round(backfillProgress.progress_pct || 0)}% complete)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelBackfillDialogOpen(false)} disabled={cancellingBackfill}>
              Keep Running
            </Button>
            <Button variant="destructive" onClick={handleCancelBackfill} disabled={cancellingBackfill}>
              {cancellingBackfill ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Backfill'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Authorization Confirmation Dialog */}
      <Dialog open={revokeAuthDialogOpen} onOpenChange={setRevokeAuthDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Authorization</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke this authorization? This will:
              <ul className="list-disc ml-4 mt-2">
                <li>Remove the OAuth tokens from the system</li>
                <li>Unlink this authorization from the integration</li>
                <li>Require re-authorization to use this integration</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeAuthDialogOpen(false)} disabled={revokingAuth}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeAuthorization} disabled={revokingAuth}>
              {revokingAuth ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Revoking...
                </>
              ) : (
                'Revoke Authorization'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}